import { useRef, useState, useMemo, useEffect } from "react";
import {
  Box,
  Card,
  CardHeader,
  CardBody,
  Heading,
  HStack,
  VStack,
  Text,
  Flex,
  Grid,
  Button,
  Select,
  IconButton,
  Tabs,
  TabList,
  Tab,
  Skeleton,
  Badge,
  useBreakpointValue,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
} from "@chakra-ui/react";
import {
  Building2,
  Phone,
  Banknote,
  Pencil,
  Calendar,
  Check,
  X,
  Settings,
} from "lucide-react";
import { Icon } from "@chakra-ui/react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { AdminLayout } from "@/components/layout/AdminLayout";
import {
  DataTable,
  type DataTableRef,
  type DataTableOptions,
} from "@/components/tables";
import { useToastNotification } from "@/lib/utils/useToastNotification";
import { useShippingFinancialStatementPermissionFlags } from "@/lib/navigation/shippingFinancialStatementPermissions";
import { useThemeColors } from "@/hooks/useThemeColors";
import { createNameRenderer } from "@/lib/utils/tableRenderers";
import { apiClient } from "@/lib/api/client";
import { buildFinancialStatementsQuery } from "@/lib/navigation/financialStatementsSearchParams";

type TabValue =
  | "bookings"
  | "statements-of-account"
  | "billing-statements"
  | "invoices";

const TAB_VALUES: TabValue[] = [
  "bookings",
  "statements-of-account",
  "billing-statements",
  "invoices",
];

const FINANCIAL_STATEMENTS_STATE_KEY = (shippingLineId: string) =>
  `financial-statements-state-${shippingLineId}`;

type BookingListRow = {
  has_soa?: boolean;
  soa_id?: number | null;
  soa_dli_sa_number?: string | null;
};

/** Y-m-d in the user's local timezone (avoids UTC shift from toISOString). */
function formatLocalDateYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function FinancialStatementsPage() {
  const colors = useThemeColors();
  const navigate = useNavigate();
  const summaryCardIconSize = useBreakpointValue({ base: 14, lg: 18 }) ?? 18;
  const { shippingLineId } = useParams<{ shippingLineId: string }>();
  const tableRef = useRef<DataTableRef>(null);
  const { showError, showSuccess, showWarning } = useToastNotification();
  const {
    isOpen: isInvalidBookingsModalOpen,
    onOpen: onInvalidBookingsModalOpen,
    onClose: onInvalidBookingsModalClose,
  } = useDisclosure();

  const navFlags = useShippingFinancialStatementPermissionFlags();

  const [activeTab, setActiveTab] = useState<TabValue>("bookings");
  const [invalidBookings, setInvalidBookings] = useState<
    Array<{ reference_number: string; reason: string }>
  >([]);

  const [clientInfo, setClientInfo] = useState({
    name: "",
    phone: "",
    taxPercentage: 0,
  });

  const [financialSummary, setFinancialSummary] = useState({
    totalCost: 0,
    remainingBalance: 0,
    totalPaid: 0,
  });

  const [isLoadingClient, setIsLoadingClient] = useState(true);
  const [isLoadingSummary, setIsLoadingSummary] = useState(true);

  // --- FILTER STATE ---
  const [filterType, setFilterType] = useState<"weekly" | "monthly">("weekly");
  const [currentDate, setCurrentDate] = useState(new Date());

  // Ref used so deferred persist writes the latest state (after restore has applied)
  const stateToPersistRef = useRef({ activeTab, filterType, currentDate });
  stateToPersistRef.current = { activeTab, filterType, currentDate };

  // Restore tab and date filter from sessionStorage when returning to this page (e.g. after success/cancel on child pages)
  useEffect(() => {
    if (!shippingLineId) return;
    const key = FINANCIAL_STATEMENTS_STATE_KEY(shippingLineId);
    try {
      const raw = sessionStorage.getItem(key);
      if (!raw) return;
      const parsed = JSON.parse(raw) as {
        activeTab?: string;
        filterType?: string;
        currentDate?: number;
      } | null;
      if (!parsed) return;
      if (
        parsed.activeTab &&
        TAB_VALUES.includes(parsed.activeTab as TabValue)
      ) {
        setActiveTab(parsed.activeTab as TabValue);
      }
      if (
        parsed.filterType === "monthly" ||
        parsed.filterType === "weekly"
      ) {
        setFilterType(parsed.filterType);
      }
      if (
        typeof parsed.currentDate === "number" &&
        !Number.isNaN(parsed.currentDate)
      ) {
        const d = new Date(parsed.currentDate);
        if (!Number.isNaN(d.getTime())) setCurrentDate(d);
      }
    } catch {
      // ignore invalid stored state
    }
  }, [shippingLineId]);

  // Persist tab and date filter after state has settled (so restore does not get overwritten by default state)
  useEffect(() => {
    if (!shippingLineId) return;
    const key = FINANCIAL_STATEMENTS_STATE_KEY(shippingLineId);
    const id = setTimeout(() => {
      const { activeTab: tab, filterType: ft, currentDate: cd } = stateToPersistRef.current;
      sessionStorage.setItem(
        key,
        JSON.stringify({
          activeTab: tab,
          filterType: ft,
          currentDate: cd.getTime(),
        }),
      );
    }, 0);
    return () => clearTimeout(id);
  }, [shippingLineId, activeTab, filterType, currentDate]);

  // --- NAVIGATION LOGIC ---
  const adjustDate = (direction: number) => {
    const newDate = new Date(currentDate);
    if (filterType === "weekly") {
      newDate.setDate(newDate.getDate() + direction * 7);
    } else {
      newDate.setMonth(newDate.getMonth() + direction);
    }
    setCurrentDate(newDate);
  };

  const dateRangeDisplay = useMemo(() => {
    const start = new Date(currentDate);
    if (filterType === "weekly") {
      const day = start.getDay();
      const diff = start.getDate() - day + (day === 0 ? -6 : 1);
      start.setDate(diff);
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      return `${start.toLocaleDateString("en-US", { month: "short", day: "numeric" })} - ${end.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;
    }
    return start.toLocaleDateString("en-US", {
      month: "long",
      year: "numeric",
    });
  }, [currentDate, filterType]);

  // Calculate expected_from_date and expected_to_date from date range
  const dateRange = useMemo(() => {
    const start = new Date(currentDate);
    let end = new Date(currentDate);

    if (filterType === "weekly") {
      const day = start.getDay();
      const diff = start.getDate() - day + (day === 0 ? -6 : 1);
      start.setDate(diff);
      end = new Date(start);
      end.setDate(start.getDate() + 6);
    } else {
      // Monthly: first day of month to last day of month
      start.setDate(1);
      end = new Date(start.getFullYear(), start.getMonth() + 1, 0);
    }

    return {
      expected_from_date: formatLocalDateYmd(start),
      expected_to_date: formatLocalDateYmd(end),
    };
  }, [currentDate, filterType]);

  const orderedTabs = useMemo((): TabValue[] => {
    const tabs: TabValue[] = ["bookings"];
    if (navFlags.soaView) tabs.push("statements-of-account");
    if (navFlags.billingView) tabs.push("billing-statements");
    if (navFlags.invoiceView) tabs.push("invoices");
    return tabs;
  }, [navFlags.soaView, navFlags.billingView, navFlags.invoiceView]);

  useEffect(() => {
    if (!orderedTabs.includes(activeTab)) {
      setActiveTab("bookings");
    }
  }, [orderedTabs, activeTab]);

  // Fetch company info from API (only when shippingLineId changes)
  useEffect(() => {
    if (!shippingLineId) return;
    let cancelled = false;
    const fetchShippingLineInfo = async () => {
      try {
        setIsLoadingClient(true);
        const shippingLineResponse = await apiClient
          .get(`/shipping-lines/${shippingLineId}`)
          .catch(() => ({ data: null }));

        if (cancelled) return;
        const shippingLineData =
          shippingLineResponse.data?.data || shippingLineResponse.data || {};
        setClientInfo({
          name: shippingLineData.name || "",
          phone: shippingLineData.contact_mobile || "",
          taxPercentage: shippingLineData.tax_percent || 0,
        });
      } catch (error: unknown) {
        if (!cancelled) showError(error);
      } finally {
        if (!cancelled) setIsLoadingClient(false);
      }
    };

    fetchShippingLineInfo();
    return () => {
      cancelled = true;
    };
  }, [shippingLineId, showError]);

  // Fetch bookings data for financial summary (only when on bookings tab)
  useEffect(() => {
    if (!shippingLineId) return;
    let cancelled = false;
    const fetchBookingsData = async () => {
      setIsLoadingSummary(true);
      try {
        const bookingsResponse = await apiClient
          .get(`/bookings/by-shipping-line/${shippingLineId}`, {
            params: {
              expected_date_from: dateRange.expected_from_date,
              expected_date_to: dateRange.expected_to_date,
            },
          })
          .catch(() => ({ data: null }));

        if (cancelled) return;
        // Get financial summary from API response (outside data param)
        const bookingsResponseData = bookingsResponse.data || {};

        setFinancialSummary({
          totalCost:
            parseFloat(bookingsResponseData.total_cost) ||
            parseFloat(bookingsResponseData.totalCost) ||
            0,
          remainingBalance:
            parseFloat(bookingsResponseData.remaining_balance) ||
            parseFloat(bookingsResponseData.remainingBalance) ||
            0,
          totalPaid:
            parseFloat(bookingsResponseData.total_paid) ||
            parseFloat(bookingsResponseData.totalPaid) ||
            0,
        });
      } catch (error: unknown) {
        if (!cancelled) showError(error);
      } finally {
        if (!cancelled) setIsLoadingSummary(false);
      }
    };

    fetchBookingsData();
    return () => {
      cancelled = true;
    };
  }, [shippingLineId, dateRange, showError]);

  const handleGenerateSOA = async () => {
    if (activeTab !== "bookings") {
      showWarning("Please select bookings from the Bookings tab");
      return;
    }

    const selectedIds = tableRef.current?.getSelectedRows() || [];
    if (selectedIds.length === 0) {
      showWarning("Please select at least one booking");
      return;
    }

    try {
      // Validate bookings before navigating
      const response = await apiClient.post("/soa-billing-check/validate", {
        booking_ids: selectedIds,
        type: 1,
      });

      const responseData = response.data?.data || response.data || {};

      // Check if there are invalid bookings
      if (
        responseData.invalid_bookings &&
        responseData.invalid_bookings.length > 0
      ) {
        // Map invalid bookings to display format
        const invalidBookingsList = responseData.invalid_bookings.map(
          (invBooking: any) => ({
            reference_number:
              invBooking.booking.reference_number ||
              invBooking.booking.referenceNumber ||
              "",
            reason:
              invBooking.reason || invBooking.message || "Invalid booking",
          }),
        );

        setInvalidBookings(invalidBookingsList);
        onInvalidBookingsModalOpen();
        return;
      }

      // If validation passes, navigate to create SOA page
      navigate(
        `/shipping-lines/financial-statements/${shippingLineId}/soa/create${buildFinancialStatementsQuery({
          shippingLineName: clientInfo.name,
          expectedDateFrom: dateRange.expected_from_date,
          expectedDateTo: dateRange.expected_to_date,
        })}`,
        { state: { selectedBookingIds: selectedIds } },
      );
    } catch (error: any) {
      // Show error and don't navigate
      showError(error);
    }
  };

  const handleGenerateInvoice = async () => {
    if (activeTab !== "statements-of-account") {
      showWarning("Please select SOAs from the Statements of Account tab");
      return;
    }

    const selectedIds = tableRef.current?.getSelectedRows() || [];
    if (selectedIds.length === 0) {
      showWarning("Please select at least one Statement of Account");
      return;
    }

    try {
      const response = await apiClient.post("/soa-billing-check/validate", {
        statement_of_account_ids: selectedIds,
        type: 3,
      });
      const responseData = response.data?.data ?? response.data ?? {};
      if (
        responseData.invalid_bookings &&
        responseData.invalid_bookings.length > 0
      ) {
        const invalidBookingsList = responseData.invalid_bookings.map(
          (invBooking: any) => ({
            reference_number:
              invBooking.booking?.reference_number ??
              invBooking.booking?.referenceNumber ??
              "",
            reason:
              invBooking.reason ?? invBooking.message ?? "Invalid booking",
          }),
        );
        setInvalidBookings(invalidBookingsList);
        onInvalidBookingsModalOpen();
        return;
      }

      navigate(
        `/shipping-lines/financial-statements/${shippingLineId}/invoice/create${buildFinancialStatementsQuery(
          {
            shippingLineName: clientInfo.name,
            soaIds: selectedIds,
          },
        )}`,
      );
    } catch (error: unknown) {
      showError(error);
    }
  };

  const handleGenerateBillingFromSoa = async (row: { id: number }) => {
    try {
      const response = await apiClient.post("/soa-billing-check/validate", {
        statement_of_account_id: row.id,
        type: 2,
      });
      const responseData = response.data?.data ?? response.data ?? {};
      if (
        responseData.invalid_bookings &&
        responseData.invalid_bookings.length > 0
      ) {
        const invalidBookingsList = responseData.invalid_bookings.map(
          (invBooking: any) => ({
            reference_number:
              invBooking.booking?.reference_number ??
              invBooking.booking?.referenceNumber ??
              "",
            reason:
              invBooking.reason ?? invBooking.message ?? "Invalid booking",
          }),
        );
        setInvalidBookings(invalidBookingsList);
        onInvalidBookingsModalOpen();
        return;
      }
      navigate(
        `/shipping-lines/financial-statements/${shippingLineId}/billing/create${buildFinancialStatementsQuery({
          shippingLineName: clientInfo.name,
          soaId: row.id,
        })}`,
      );
    } catch (error: unknown) {
      showError(error);
    }
  };

  const tableOptions: DataTableOptions = useMemo(() => {
    const getDataSource = () => {
      switch (activeTab) {
        case "bookings":
          return `/bookings/by-shipping-line/${shippingLineId}`;
        case "statements-of-account":
          return "/soa";
        case "billing-statements":
          return "/billing-statements";
        case "invoices":
          return "/invoices";
        default:
          return "/bookings";
      }
    };

    const getNoRecordText = () => {
      switch (activeTab) {
        case "bookings":
          return "No bookings found";
        case "statements-of-account":
          return "No statements of account found";
        case "billing-statements":
          return "No billing statements found";
        case "invoices":
          return "No invoices found";
        default:
          return "No records found";
      }
    };

    const getDataFields = (): Record<
      string,
      import("@/components/tables").DataField
    > => {
      switch (activeTab) {
        case "bookings":
          return {
            created_at: {
              name: "Created Date",
              maxWidth: "150px",
              withSort: true,
            },
            reference_number: {
              name: "Reference No",
              maxWidth: "150px",
              withSort: true,
            },
            cypa_from: {
              name: "Pickup",
              maxWidth: "200px",
              withSort: true,
              sortField: "cypa_id_from",
            },
            cypa_to: {
              name: "Dropoff",
              maxWidth: "200px",
              withSort: true,
              sortField: "cypa_id_to",
            },
            vessel: {
              name: "Vessel",
              maxWidth: "150px",
              withSort: true,
            },
            expected_date: {
              name: "Expected Date",
              maxWidth: "100px",
              withSort: true,
            },
            prepared_by: {
              name: "Prepared By",
              maxWidth: "150px",
              withSort: true,
            },
          };
        case "statements-of-account":
          return {
            created_at: {
              name: "Created Date",
              maxWidth: "150px",
              withSort: true,
            },
            dli_sa_number: {
              name: "DLI-SA No",
              maxWidth: "200px",
              withSort: true,
            },
            work_order: {
              name: "Work Order No",
              maxWidth: "200px",
              withSort: true,
            },
            total_amount: {
              name: "Total Amount",
              maxWidth: "200px",
              withSort: true,
            },
          };
        case "billing-statements":
          return {
            created_at: {
              name: "Created Date",
              maxWidth: "150px",
              withSort: true,
            },
            billing_statement_no: {
              name: "Billing Statement No",
              maxWidth: "200px",
              withSort: true,
            },
            payment_term: {
              name: "Payment Term",
              maxWidth: "150px",
              withSort: true,
            },
            bus_style: {
              name: "Bus Style",
              maxWidth: "150px",
              withSort: true,
            },
            ci_date: {
              name: "CI Date",
              maxWidth: "150px",
              withSort: true,
            },
            due_date: {
              name: "Due Date",
              maxWidth: "150px",
              withSort: true,
            },
            prepared_by: {
              name: "Prepared By",
              maxWidth: "150px",
              withSort: true,
            },
          };
        case "invoices":
          return {
            created_at: {
              name: "Created Date",
              maxWidth: "150px",
              withSort: true,
            },
            invoice_number: {
              name: "Invoice No",
              maxWidth: "120px",
              withSort: true,
            },
            vatable_sales: {
              name: "VATable Sales",
              maxWidth: "120px",
              withSort: true,
            },
            vat: {
              name: "VAT",
              maxWidth: "120px",
              withSort: true,
            },
            total_sales_inclusive: {
              name: "Total Sales (VAT Inclusive)",
              maxWidth: "120px",
              withSort: true,
            },
            discount: {
              name: "Discount",
              maxWidth: "100px",
              withSort: true,
            },
            less_withdrawing_tax: {
              name: "Withholding Tax",
              maxWidth: "120px",
              withSort: true,
            },
            total_amount: {
              name: "Total Amount",
              maxWidth: "120px",
              withSort: true,
            },
          };
        default:
          return {};
      }
    };

    const getOtherActions = () => {
      if (activeTab === "statements-of-account") {
        if (!navFlags.soaView) return [];
        const actions: Array<{ name: string; onClick: (row: any) => void }> =
          [];
        if (navFlags.soaEdit) {
          actions.push({
            name: "Edit SOA",
            onClick: (row: any) => {
              navigate(
                `/shipping-lines/financial-statements/${shippingLineId}/soa/${row.id}${buildFinancialStatementsQuery({
                  shippingLineName: clientInfo.name,
                })}`,
              );
            },
          });
        }
        if (navFlags.soaDownload) {
          actions.push({
            name: "Download SOA",
            onClick: (row: any) => {
              navigate(
                `/shipping-lines/financial-statements/${shippingLineId}/soa/${row.id}/download${buildFinancialStatementsQuery({
                  shippingLineName: clientInfo.name,
                })}`,
              );
            },
          });
        }
        if (navFlags.soaEmail) {
          actions.push({
            name: "Send SOA to Client",
            onClick: (row: any) => {
              navigate(
                `/shipping-lines/financial-statements/${shippingLineId}/soa/${row.id}/email${buildFinancialStatementsQuery({
                  shippingLineName: clientInfo.name,
                })}`,
              );
            },
          });
        }
        if (navFlags.billingCreate) {
          actions.push({
            name: "Generate Billing Statement",
            onClick: (row: any) => handleGenerateBillingFromSoa(row),
          });
        }
        return actions;
      }
      if (activeTab === "invoices") {
        if (!navFlags.invoiceView) return [];
        const actions: Array<{ name: string; onClick: (row: any) => void }> =
          [];
        if (navFlags.invoiceEdit) {
          actions.push({
            name: "Edit Invoice",
            onClick: (row: any) => {
              navigate(
                `/shipping-lines/financial-statements/${shippingLineId}/invoice/${row.id}${buildFinancialStatementsQuery({
                  shippingLineName: clientInfo.name,
                })}`,
              );
            },
          });
        }
        if (navFlags.invoiceDownload) {
          actions.push({
            name: "Download Invoice",
            onClick: (row: any) => {
              navigate(
                `/shipping-lines/financial-statements/${shippingLineId}/invoice/${row.id}/download${buildFinancialStatementsQuery({
                  shippingLineName: clientInfo.name,
                })}`,
              );
            },
          });
        }
        if (navFlags.invoiceEmail) {
          actions.push({
            name: "Send Invoice to Client",
            onClick: (row: any) => {
              navigate(
                `/shipping-lines/financial-statements/${shippingLineId}/invoice/${row.id}/email${buildFinancialStatementsQuery({
                  shippingLineName: clientInfo.name,
                })}`,
              );
            },
          });
        }
        return actions;
      }
      if (activeTab === "billing-statements") {
        if (!navFlags.billingView) return [];
        const actions: Array<{ name: string; onClick: (row: any) => void }> =
          [];
        if (navFlags.billingEdit) {
          actions.push({
            name: "Edit Billing Statement",
            onClick: (row: any) => {
              navigate(
                `/shipping-lines/financial-statements/${shippingLineId}/billing/${row.id}${buildFinancialStatementsQuery({
                  shippingLineName: clientInfo.name,
                  soaId: row.statement_of_account_id,
                })}`,
              );
            },
          });
        }
        if (navFlags.billingDownload) {
          actions.push({
            name: "Download Billing Statement",
            onClick: (row: any) => {
              navigate(
                `/shipping-lines/financial-statements/${shippingLineId}/billing/${row.id}/download${buildFinancialStatementsQuery({
                  shippingLineName: clientInfo.name,
                })}`,
              );
            },
          });
        }
        if (navFlags.billingDownloadSoa) {
          actions.push({
            name: "Download Billing Statement and SOA",
            onClick: (row: any) => {
              navigate(
                `/shipping-lines/financial-statements/${shippingLineId}/billing/${row.id}/download-soa-and-billing${buildFinancialStatementsQuery({
                  shippingLineName: clientInfo.name,
                  soaId: row.statement_of_account_id,
                })}`,
              );
            },
          });
        }
        if (navFlags.billingEmail) {
          actions.push({
            name: "Send Billing Statement to Client",
            onClick: (row: any) => {
              navigate(
                `/shipping-lines/financial-statements/${shippingLineId}/billing/${row.id}/email${buildFinancialStatementsQuery({
                  shippingLineName: clientInfo.name,
                })}`,
              );
            },
          });
        }
        if (navFlags.billingEmailSoa) {
          actions.push({
            name: "Send Billing Statement and SOA to Client",
            onClick: (row: any) => {
              navigate(
                `/shipping-lines/financial-statements/${shippingLineId}/billing/${row.id}/email-soa-and-billing${buildFinancialStatementsQuery({
                  shippingLineName: clientInfo.name,
                  soaId: row.statement_of_account_id,
                })}`,
              );
            },
          });
        }
        return actions;
      }
      return undefined;
    };

    const rowOtherActions = getOtherActions();

    return {
      dataFields: getDataFields(),
      dataSource: getDataSource(),
      primaryKey: "id",
      softDelete: true,
      displayInModal: false,
      noRecordText: getNoRecordText(),
      showCheckboxes:
        activeTab === "bookings" || activeTab === "statements-of-account",
      showActionsColumn:
        activeTab !== "bookings" && (rowOtherActions?.length ?? 0) > 0,
      otherActions: rowOtherActions,
    };
  }, [
    activeTab,
    shippingLineId,
    clientInfo.name,
    navigate,
    dateRange.expected_from_date,
    dateRange.expected_to_date,
    navFlags,
  ]);

  const permissions = useMemo(
    () => ({
      can_edit: false,
      can_delete: false,
      can_create:
        activeTab === "bookings" ? navFlags.soaCreate : false,
      can_view: navFlags.canViewFinancialStatementsPage,
      can_export: false,
      can_import: false,
    }),
    [
      navFlags.canViewFinancialStatementsPage,
      activeTab,
      navFlags.soaCreate,
    ],
  );

  const tableParams = useMemo(() => {
    const perPage = { per_page: 20 };
    if (activeTab === "bookings") {
      return {
        ...perPage,
        shipping_line_id: shippingLineId,
        expected_date_from: dateRange.expected_from_date,
        expected_date_to: dateRange.expected_to_date,
        is_complete: 0,
      };
    }
    if (
      activeTab === "statements-of-account" ||
      activeTab === "billing-statements" ||
      activeTab === "invoices"
    ) {
      return {
        ...perPage,
        shipping_line_id: shippingLineId,
        date_from: dateRange.expected_from_date,
        date_to: dateRange.expected_to_date,
      };
    }
    // For other tabs, return empty params
    return perPage;
  }, [activeTab, shippingLineId, dateRange]);

  const customRenderers = useMemo(
    () => {
      const formatAmount = (value: any) => {
        if (value == null || value === "") return "";
        const n = Number(value);
        return Number.isNaN(n) ? String(value) : n.toFixed(2);
      };
      return {
      reference_number: (value: string, row?: BookingListRow) => {
        const hasSoa = row?.has_soa === true;
        const soaLabel =
          row?.soa_dli_sa_number ??
          (row?.soa_id != null ? `#${row.soa_id}` : null);
        const canNavigateToSoa =
          hasSoa && row?.soa_id != null && navFlags.soaView;

        return (
          <HStack spacing={2} flexWrap="wrap">
            <Text fontSize="sm">{value ?? ""}</Text>
            {hasSoa && soaLabel && (
              <Badge
                colorScheme="purple"
                variant="subtle"
                fontSize="xs"
                fontWeight="medium"
                title={`Statement of Account: ${soaLabel}`}
                cursor={canNavigateToSoa ? "pointer" : "default"}
                onClick={
                  canNavigateToSoa
                    ? (event) => {
                        event.stopPropagation();
                        navigate(
                          `/shipping-lines/financial-statements/${shippingLineId}/soa/${row.soa_id}${buildFinancialStatementsQuery({
                            shippingLineName: clientInfo.name,
                          })}`,
                        );
                      }
                    : undefined
                }
              >
                SOA: {soaLabel}
              </Badge>
            )}
          </HStack>
        );
      },
      cypa_from: createNameRenderer(true),
      cypa_to: createNameRenderer(true),
      // Invoice created_at renderer - format as yyyy-mm-dd HH:mm:ss, wrap to show full date in small space
      created_at: (value: any) => {
        if (!value) return "";
        try {
          const date = new Date(value);
          if (isNaN(date.getTime())) return value;

          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, "0");
          const day = String(date.getDate()).padStart(2, "0");
          const hours = String(date.getHours()).padStart(2, "0");
          const minutes = String(date.getMinutes()).padStart(2, "0");
          const seconds = String(date.getSeconds()).padStart(2, "0");

          const formatted = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
          return (
            <Box
              maxW="150px"
              wordBreak="break-word"
              whiteSpace="normal"
              overflowWrap="break-word"
            >
              {formatted}
            </Box>
          );
        } catch {
          return value;
        }
      },
      vatable_sales: (value: any) => formatAmount(value),
      vat: (value: any) => formatAmount(value),
      total_sales_inclusive: (value: any) => formatAmount(value),
      discount: (value: any) => formatAmount(value),
      less_withdrawing_tax: (value: any) => formatAmount(value),
      total_amount: (value: any) => formatAmount(value),
    };
    },
    [
      clientInfo.name,
      navigate,
      navFlags.soaView,
      shippingLineId,
    ],
  );

  const datePickerId = "financial-date-picker";

  return (
    <AdminLayout>
      <Box
        p={4}
        h="100%"
        overflow="hidden"
        display="flex"
        flexDirection="column"
      >
        <VStack spacing={0} align="stretch" flex="1" overflow="hidden" minH={0}>
          <Card
            flexShrink={0}
            display="flex"
            flexDirection="column"
            flex="1"
            minH={0}
            overflow="hidden"
          >
            <CardHeader flexShrink={0} py={2} px={{ base: 4, md: 6 }}>
              <HStack spacing={2} align="center" mb={0.5}>
                <IconButton
                  aria-label="Back"
                  icon={<Icon as={ChevronLeft} boxSize={6} />}
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate("/shipping-lines")}
                />
                <Heading size="md">Financial Statements</Heading>
              </HStack>
              <Text fontSize="sm" color={colors.text} ml={10}>
                Manage statements of account, billing statements, and invoices
              </Text>
            </CardHeader>

            <CardBody
              px={{ base: 4, md: 6 }}
              pt={2}
              pb={2}
              flex="1"
              overflow="hidden"
              display="flex"
              flexDirection="column"
              minH={0}
            >
              {/* Row 1: 2-column — Company info (left) | Financial summary cards (right, always 3 horizontal) */}
              <Flex
                gap={{ base: 6, md: 8 }}
                align="flex-start"
                wrap="nowrap"
                overflowX="auto"
                overflowY="hidden"
                sx={{
                  WebkitOverflowScrolling: "touch",
                  scrollbarWidth: "thin",
                }}
                mt={3}
                mb={3}
              >
                <Box w="320px" minW="320px" maxW="320px" flex="0 0 320px">
                  {isLoadingClient ? (
                    <VStack align="flex-start" spacing={3}>
                      <HStack spacing={2}>
                        <Skeleton w="32px" h="32px" borderRadius="md" />
                        <Skeleton height="14px" width="100px" />
                      </HStack>
                      <HStack
                        spacing={2}
                        flexWrap="nowrap"
                        overflowX="auto"
                        overflowY="hidden"
                        sx={{
                          WebkitOverflowScrolling: "touch",
                          scrollbarWidth: "thin",
                        }}
                      >
                        <Skeleton
                          height="24px"
                          width="90px"
                          borderRadius="md"
                        />
                        <Skeleton
                          height="24px"
                          width="100px"
                          borderRadius="md"
                        />
                      </HStack>
                    </VStack>
                  ) : (
                    <VStack align="flex-start" spacing={3}>
                      <HStack spacing={2}>
                        <Box
                          w="32px"
                          h="32px"
                          flexShrink={0}
                          borderRadius="md"
                          bg={colors.sectionBg}
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          <Icon as={Building2} boxSize={5} color="blue.600" />
                        </Box>
                        <Heading
                          size="md"
                          fontWeight="bold"
                          color="blue.600"
                          noOfLines={2}
                        >
                          {clientInfo.name}
                        </Heading>
                      </HStack>
                      <HStack
                        spacing={2}
                        flexWrap="nowrap"
                        overflowX="auto"
                        overflowY="hidden"
                        sx={{
                          WebkitOverflowScrolling: "touch",
                          scrollbarWidth: "thin",
                        }}
                      >
                        {clientInfo.phone && (
                          <Card
                            bg={colors.cardBg}
                            borderRadius="md"
                            px={2}
                            py={1}
                            borderWidth="1px"
                            borderColor={colors.border}
                          >
                            <HStack spacing={1}>
                              <Phone
                                size={14}
                                style={{ color: colors.textMuted }}
                              />
                              <Text fontSize="xs" color={colors.text}>
                                {clientInfo.phone}
                              </Text>
                            </HStack>
                          </Card>
                        )}
                        {clientInfo.taxPercentage > 0 && (
                          <Card
                            bg={colors.cardBg}
                            borderRadius="md"
                            px={2}
                            py={1}
                            borderWidth="1px"
                            borderColor={colors.border}
                          >
                            <HStack spacing={1}>
                              <Banknote
                                size={14}
                                style={{ color: colors.textMuted }}
                              />
                              <Text fontSize="xs" color={colors.text}>
                                +{clientInfo.taxPercentage}% Tax
                              </Text>
                            </HStack>
                          </Card>
                        )}
                      </HStack>
                    </VStack>
                  )}
                </Box>
                <Grid
                  templateColumns="repeat(3, 1fr)"
                  gap={2}
                  minW={0}
                  flex="1"
                >
                  {isLoadingSummary ? (
                    <>
                      <Skeleton height="52px" minW={0} borderRadius="md" />
                      <Skeleton height="52px" minW={0} borderRadius="md" />
                      <Skeleton height="52px" minW={0} borderRadius="md" />
                    </>
                  ) : (
                    <>
                      <Card
                        bg={colors.cardBg}
                        borderRadius="md"
                        p={2}
                        minW={0}
                        borderWidth="1px"
                        borderColor={colors.border}
                      >
                        <VStack align="flex-start" spacing={0.5}>
                          <Text fontSize="sm" color={colors.text}>
                            Total Cost
                          </Text>
                          <HStack spacing={1}>
                            <Banknote
                              size={summaryCardIconSize}
                              style={{ color: colors.text }}
                            />
                            <Text
                              fontSize="sm"
                              fontWeight="medium"
                              color={colors.text}
                              noOfLines={1}
                            >
                              P{" "}
                              {financialSummary.totalCost.toLocaleString(
                                "en-US",
                              )}
                            </Text>
                          </HStack>
                        </VStack>
                      </Card>
                      <Card
                        bg={colors.cardBg}
                        borderRadius="md"
                        p={2}
                        minW={0}
                        borderWidth="1px"
                        borderColor={colors.border}
                      >
                        <VStack align="flex-start" spacing={0.5}>
                          <Text
                            fontSize="sm"
                            color="red.500"
                            fontWeight="medium"
                          >
                            Remaining
                          </Text>
                          <HStack spacing={1}>
                            <Banknote
                              size={summaryCardIconSize}
                              style={{ color: "var(--chakra-colors-red-500)" }}
                            />
                            <Text
                              fontSize="sm"
                              fontWeight="medium"
                              color="red.500"
                              noOfLines={1}
                            >
                              P{" "}
                              {financialSummary.remainingBalance.toLocaleString(
                                "en-US",
                              )}
                            </Text>
                          </HStack>
                        </VStack>
                      </Card>
                      <Card
                        bg={colors.cardBg}
                        borderRadius="md"
                        p={2}
                        minW={0}
                        borderWidth="1px"
                        borderColor={colors.border}
                      >
                        <VStack align="flex-start" spacing={0.5}>
                          <Text
                            fontSize="sm"
                            color="blue.500"
                            fontWeight="medium"
                          >
                            Total Paid
                          </Text>
                          <HStack spacing={1}>
                            <Banknote
                              size={summaryCardIconSize}
                              style={{ color: "var(--chakra-colors-blue-500)" }}
                            />
                            <Text
                              fontSize="sm"
                              fontWeight="medium"
                              color="blue.500"
                              noOfLines={1}
                            >
                              P{" "}
                              {financialSummary.totalPaid.toLocaleString(
                                "en-US",
                              )}
                            </Text>
                          </HStack>
                        </VStack>
                      </Card>
                    </>
                  )}
                </Grid>
              </Flex>

              {/* Row 2: Date range picker + Generate SOA / Generate Invoice (mirrors multi-select booking → SOA flow) */}
              <Flex
                direction={{ base: "column", sm: "row" }}
                gap={2}
                align={{ base: "flex-start", sm: "center" }}
                flexWrap="wrap"
                mt={3}
                mb={3}
              >
                <HStack spacing={2} flexShrink={0}>
                  <Select
                    size="sm"
                    w={{ base: "100%", sm: "100px" }}
                    maxW="120px"
                    borderRadius="md"
                    value={filterType}
                    onChange={(e) =>
                      setFilterType(e.target.value as "weekly" | "monthly")
                    }
                  >
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                  </Select>
                  <HStack spacing={0.5} flexShrink={0}>
                    <IconButton
                      icon={<ChevronLeft size={16} />}
                      aria-label="Prev"
                      size="sm"
                      variant="ghost"
                      onClick={() => adjustDate(-1)}
                    />
                    <HStack
                      border="1px solid"
                      borderColor={colors.border}
                      px={2}
                      py={1}
                      borderRadius="md"
                      bg={colors.cardBg}
                      cursor="pointer"
                      minW={0}
                      onClick={() =>
                        (
                          document.getElementById(
                            datePickerId,
                          ) as HTMLInputElement
                        )?.showPicker()
                      }
                    >
                      <Icon
                        as={Calendar}
                        boxSize={4}
                        color={colors.textMuted}
                      />
                      <Text
                        fontSize="sm"
                        fontWeight="medium"
                        color={colors.text}
                        noOfLines={1}
                      >
                        {dateRangeDisplay}
                      </Text>
                      <input
                        id={datePickerId}
                        type="date"
                        style={{
                          width: 0,
                          height: 0,
                          opacity: 0,
                          position: "absolute",
                        }}
                        onChange={(e) =>
                          e.target.value &&
                          setCurrentDate(new Date(e.target.value))
                        }
                      />
                    </HStack>
                    <IconButton
                      icon={<ChevronRight size={16} />}
                      aria-label="Next"
                      size="sm"
                      variant="ghost"
                      onClick={() => adjustDate(1)}
                    />
                  </HStack>
                </HStack>
                {activeTab === "bookings" && navFlags.soaCreate && (
                  <Box flexShrink={0} ml="auto">
                    <Button
                      leftIcon={<Settings size={16} />}
                      colorScheme="blue"
                      size="sm"
                      onClick={handleGenerateSOA}
                    >
                      Generate SOA
                    </Button>
                  </Box>
                )}
                {activeTab === "statements-of-account" &&
                  navFlags.invoiceCreate && (
                    <Box flexShrink={0} ml="auto">
                      <Button
                        leftIcon={<Settings size={16} />}
                        colorScheme="blue"
                        size="sm"
                        onClick={handleGenerateInvoice}
                      >
                        Generate Invoice
                      </Button>
                    </Box>
                  )}
              </Flex>

              {/* Tabs + table */}
              <Box
                mt={6}
                flex="1"
                overflow="hidden"
                display="flex"
                flexDirection="column"
                minH={0}
              >
                <Tabs
                  variant="line"
                  colorScheme="blue"
                  index={Math.max(0, orderedTabs.indexOf(activeTab))}
                  onChange={(index) => {
                    setActiveTab(orderedTabs[index] ?? "bookings");
                  }}
                  display="flex"
                  flexDirection="column"
                  h="auto"
                  minH={0}
                >
                  <TabList px={0} pt={0} pb={2} flexShrink={0} gap={1}>
                    {orderedTabs.map((tab) => (
                      <Tab
                        key={tab}
                        fontSize={{ base: "xs", sm: "sm" }}
                        whiteSpace="nowrap"
                        _selected={{
                          fontWeight: "bold",
                          borderBottomWidth: "2px",
                          borderBottomColor: "blue.500",
                        }}
                      >
                        {tab === "bookings"
                          ? "Bookings"
                          : tab === "statements-of-account"
                            ? "Statements of Account"
                            : tab === "billing-statements"
                              ? "Billing Statements"
                              : "Invoices"}
                      </Tab>
                    ))}
                  </TabList>
                </Tabs>
                <Box
                  h="100%"
                  mt={3}
                  mx={0}
                  display="flex"
                  flexDirection="column"
                  minH={0}
                >
                  <DataTable
                    ref={tableRef}
                    options={tableOptions}
                    params={tableParams}
                    access={permissions}
                    customRenderers={customRenderers}
                  />
                </Box>
              </Box>
            </CardBody>
          </Card>
        </VStack>
      </Box>

      {/* Invalid Bookings Modal */}
      <Modal
        isOpen={isInvalidBookingsModalOpen}
        onClose={onInvalidBookingsModalClose}
        size="xl"
        isCentered
      >
        <ModalOverlay />
        <ModalContent bg={colors.cardBg}>
          <ModalHeader borderBottomWidth="1px" borderColor={colors.border}>
            Invalid Bookings
          </ModalHeader>
          <ModalBody p={6}>
            <Text mb={4} color={colors.text}>
              The following booking/s cannot be used to generate SOA/ Billing Statement/ Invoice:
            </Text>
            <TableContainer pt={2}>
              <Table variant="simple" size="sm">
                <Thead bg={colors.sectionBg}>
                  <Tr>
                    <Th borderColor={colors.border} color={colors.text}>
                      Reference Number
                    </Th>
                    <Th borderColor={colors.border} color={colors.text}>
                      Reason
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {invalidBookings.map((booking, index) => (
                    <Tr key={index}>
                      <Td borderColor={colors.border} color={colors.text}>
                        {booking.reference_number}
                      </Td>
                      <Td borderColor={colors.border} color={colors.text}>
                        {booking.reason}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          </ModalBody>
          <ModalFooter borderTopWidth="1px" borderColor={colors.border}>
            <Button colorScheme="blue" onClick={onInvalidBookingsModalClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </AdminLayout>
  );
}
